import { Link } from "react-router-dom";
import { ArrowRight, Award, Heart, Target, Flag } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/data";
import { useLang } from "@/lib/i18n";

const APropos = () => {
    const { tr } = useLang();

    const VALUES = [
        { icon: Heart, t: tr("Culture d'abord", "Culture first"), d: tr("Ta nourriture raconte ton histoire. On ne l'efface pas, on la sublime.", "Your food tells your story. We don't erase it, we elevate it.") },
        { icon: Target, t: tr("Précision", "Precision"), d: tr("Chaque gramme, chaque série, chaque semaine est pensé pour ton objectif.", "Every gram, every set, every week is designed for your goal.") },
        { icon: Award, t: tr("Exigence bienveillante", "Caring high standards"), d: tr("Je te pousse à donner le meilleur, sans jamais te juger.", "I push you to give your best, without ever judging you.") },
        { icon: Flag, t: tr("Durabilité", "Sustainability"), d: tr("Pas de transformation éclair. Des résultats qui tiennent des années.", "No overnight transformation. Results that last for years.") },
    ];

    return (
        <div data-testid="apropos-page" className="pt-28 md:pt-36">
            <section className="pb-24 md:pb-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    <Reveal className="lg:col-span-5">
                        <div className="relative lg:sticky lg:top-32">
                            <div className="absolute -inset-4 border border-terracotta/30 translate-x-5 translate-y-5" />
                            <div className="relative overflow-hidden">
                                <img
                                    src={IMAGES.aboutCoach}
                                    alt="Coach Roméo"
                                    className="w-full aspect-[4/5] object-cover object-top"
                                    data-testid="apropos-coach-image"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                <div className="absolute bottom-6 left-6">
                                    <p className="font-heading text-2xl font-black uppercase tracking-tight">Coach Roméo</p>
                                    <p className="text-sm text-ochre uppercase tracking-[0.2em]">
                                        {tr("Musculation · Nutrition africaine", "Bodybuilding · African nutrition")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    <div className="lg:col-span-7">
                        <Reveal>
                            <Overline>{tr("À propos du coach", "About the coach")}</Overline>
                            <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
                                {tr("La force qui vient", "Strength that comes")}{" "}
                                <span className="font-accent italic font-medium text-ochre">{tr("des racines.", "from the roots.")}</span>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.1} className="mt-8 space-y-6 text-sand leading-relaxed font-light">
                            <p>
                                {tr(
                                    "J'ai grandi entre deux mondes : les odeurs de mafé et de sauce feuille qui sortaient de la cuisine de ma mère, et la salle de sport où je passais mes soirées. Pendant longtemps, on m'a dit qu'il fallait choisir — que « manger africain » et « être en forme » ne faisaient pas bon ménage.",
                                    "I grew up between two worlds: the smells of mafé and leaf sauce coming from my mother's kitchen, and the gym where I spent my evenings. For a long time, I was told I had to choose — that 'eating African' and 'being fit' didn't mix."
                                )}
                            </p>
                            <p>
                                {tr(
                                    "Cinq ans de coaching et plus de 250 clients accompagnés plus tard, je peux l'affirmer : c'est faux. La cuisine africaine, avec ses céréales complètes, ses légumes-feuilles, ses poissons braisés et ses légumineuses, est l'une des plus formidables alliées de la perte de gras et de la prise de muscle — à condition de savoir la calibrer.",
                                    "Five years of coaching and more than 250 clients later, I can say it: that's wrong. African cuisine, with its whole grains, leafy greens, braised fish and legumes, is one of the greatest allies of fat loss and muscle gain — as long as you know how to calibrate it."
                                )}
                            </p>
                            <p>
                                {tr(
                                    "C'est exactement ce que je t'apprends. Pas un régime importé, pas des aliments introuvables : une méthode construite autour de ton assiette, ta famille et ta vie réelle. C'est aussi tout l'objet de mon ebook, « Manger Africain, Brûler le Gras ».",
                                    "That's exactly what I teach you. Not an imported diet, not hard-to-find ingredients: a method built around your plate, your family and your real life. That's also the whole point of my ebook, 'Eat African, Burn Fat'."
                                )}
                            </p>
                        </Reveal>

                        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {VALUES.map((v, i) => (
                                <Reveal key={v.t} delay={0.15 + i * 0.08}>
                                    <div className="border border-white/10 p-6 h-full hover:border-terracotta/40 transition-colors duration-500">
                                        <v.icon className="h-6 w-6 text-terracotta" strokeWidth={1.8} />
                                        <h3 className="mt-4 font-heading font-bold uppercase tracking-tight">{v.t}</h3>
                                        <p className="mt-2 text-sm text-sand font-light leading-relaxed">{v.d}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>

                        <Reveal delay={0.3} className="mt-12">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link
                                    to="/contact"
                                    data-testid="apropos-cta-consultation"
                                    className="inline-flex items-center justify-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-terracotta-hover"
                                >
                                    {tr("Travailler avec moi", "Work with me")} <ArrowRight className="h-4 w-4" />
                                </Link>
                                <Link
                                    to="/ebook"
                                    data-testid="apropos-cta-ebook"
                                    className="inline-flex items-center justify-center gap-3 rounded-full border border-white/25 px-8 py-4 text-sm font-bold uppercase tracking-wider text-cream transition-colors duration-300 hover:border-ochre hover:text-ochre"
                                >
                                    {tr("Découvrir l'Ebook", "Discover the Ebook")}
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default APropos;
